/// <amd-module name="VSS/Features/Charts/Internal/TooltipFormatter" />

import * as Charts_Contracts from "../Contracts";
import { shiftToUTC } from "./CompatUtilities";
import * as Internal_Contracts from "./InternalContracts";
import { decodeString, encodeUntrustedString } from "./StringUtilities";

/**
 * A point on the chart to render in the tooltip.
 * This is the 'this' context object in the tooltip formatter handler if the tooltip is not shared or split.
 * One in a list if the tooltip is shared or split.
 */
export interface TooltipFormatterPoint {
    /** The point information as stored in series.data[#]. */
    point: Internal_Contracts.HighchartsExtendedPointObject;
    /** The series of the point. */
    series: Internal_Contracts.HighchartsExtendedSeriesObject;
}

/**
 * A collection of points on the chart to be shown in the tooltip.
 * This is the 'this' context object in the tooltip formatter handler if the tooltip is shared or split.
 */
export interface SharedTooltipFormatterContext {
    /** The collection of points to be represented in the tooltip */
    points: TooltipFormatterPoint[];
}

export interface TooltipFormatterOptions {
    /**
     * If set to true and the tooltip is shared across series the list of points are reversed
     * before being passed to the tooltip mapping.
     */
    reverseSeries: boolean;

    /** Defines a tooltip points to line item mapping that should be used instead of the chart default */
    tooltipMapping: (points: Charts_Contracts.DataPoint[]) => Charts_Contracts.TooltipLineItem[];

    /** Custom tooltip header */
    tooltipHeader?: (category: string | number) => string | number;
}

/** A chart that defines how to gather necesssary formatting information for the tooltip formatter. */
export interface ITooltipFormattingSupportedChart {
    /**
     * Maps data points to tooltip line items to be presented in the tooltip.
     * @param points to map to line items.
     * @returns a mapping function between data points and tooltip line items.
     */
    mapDataPointsToTooltipLineItems(points: Charts_Contracts.DataPoint[]): Charts_Contracts.TooltipLineItem[];

    /**
     * The value the tooltip formatter should use for formatting the tooltip header.
     * @param formatterPoint that is passed to the tooltip formatter by highcharts.
     * @returns the header value.
     */
    getTooltipHeaderValue(formatterPoint: TooltipFormatterPoint): string | number;

    /**
     * Used by the TooltipFormatter to extract the values of the given highchart point as an array.
     *
     * Different chart types have different values in their points,
     * so derived chart types should override this method to extract the
     * correct information from the point.
     *
     * For example, in an arearange chart the values are point.low
     * and point.high rather than point.y.
     * @param highchartsPoint from which to retrieve values.
     * @returns an array of the values of the point.
     */
    interpretPointAsValuesForTooltip(point: Internal_Contracts.HighchartsExtendedPointObject): (string | number)[];
}

/** Used for formatting tooltip. */
export class TooltipFormatter {
    public static headerFormat = '<div class="header-block">{0}</div>';

    private static isSharedTooltipContext(context: TooltipFormatterPoint | SharedTooltipFormatterContext): context is SharedTooltipFormatterContext {
        return (context as SharedTooltipFormatterContext).points != null;
    }

    private static decodeValuesToRender(values: (string | number)[]): (string | number)[] {
        return values.map(value => {
            return typeof value === "string" ? decodeString(value) : value;
        });
    }

    public static isDateTime(point: Internal_Contracts.HighchartsExtendedPointObject): boolean {
        return point.series.xAxis && point.series.xAxis.options.type === "datetime";
    }

    public static getColorBox(color: string, type: Charts_Contracts.TooltipLineItemMarkerType): string {
        // Note: SVG attributes cannot be described as CSS.
        const width = type == Charts_Contracts.TooltipLineItemMarkerType.Bar ? 4 : 12;
        color = encodeUntrustedString(color);
        return (
            `<svg width='${width}' height='12' class='tooltip-color' style='margin-right:5px; margin-top:2px; veritical-align:middle;'>` +
            `<rect xmlns='http://www.w3.org/2000/svg' fill='${color}' width='${width}' height='12' zIndex='3' /></svg>`
        );
    }

    /**
     * Build the tooltip header with the given information
     * @param value - The value to put into the header
     * @param isDateTime - is the given value a datetime, if true, it would format that using user's locale date format
     * @param formatString - The formatting string to use to formated the header. Default is <div class="header-block">{0}</div>'
     */
    public static buildTooltipHeader(value: string | number, isDateTime: boolean): string {
        let formattedValue = value;
        if (isDateTime) {
            const date = shiftToUTC(new Date(value as number));
            formattedValue = date.toLocaleDateString(navigator.language);
        }
        return `<div class="header-block">${formattedValue}</div>`;
    }

    /**
     * Build the tooltip point with the given information
     */
    public static buildTooltipPoint(item: Charts_Contracts.TooltipLineItem): string {
        // Construct the color section first
        let output = "";
        if (item.styleType) {
            if (item.styleType.renderCustomStyle) {
                output = item.styleType.renderCustomStyle(item.styleType);
            } else {
                // If type is not set, default the type to Bar
                if (!item.styleType.type) {
                    item.styleType.type = Charts_Contracts.TooltipLineItemMarkerType.Bar;
                }

                output = this.getColorBox(item.styleType.color, item.styleType.type);
            }
        }

        item.text = encodeUntrustedString(item.text);
        output += `<span style='veritical-align:middle;'>${item.text}</span></br>`;
        return output;
    }
    private _options: TooltipFormatterOptions;

    public constructor(options: TooltipFormatterOptions) {
        this._options = options;
    }

    public getFormatter(chart: ITooltipFormattingSupportedChart): () => string {
        const that: TooltipFormatter = this;
        const handler = function(this: TooltipFormatterPoint | SharedTooltipFormatterContext) {
            const context = this;

            const points = TooltipFormatter.isSharedTooltipContext(context) ? context.points.map(x => x.point) : [context.point];

            // If the tooltip is shared, sort the data if requested
            if (that._options.reverseSeries && TooltipFormatter.isSharedTooltipContext(context)) {
                points.sort(function(a, b) {
                    return b.series.index - a.series.index;
                });
            }

            let isDateTime = TooltipFormatter.isDateTime(points[0]);
            const dataPoints = points.map(point => {
                // The series name is an untrusted string that's encoded when translating series options.
                // We want to decode the series name before handing it off to the mapping function because
                // we encode the text returned by the mapping function and don't want to double encode the
                // series name if it is used.
                const decodedSeriesName = decodeString(point.series.name);

                // Normally, values are numeric, but can be textual in cases such as header cells of tables. Ensure string typed value elements are also decoded for accurate tooltip rendering.
                const valuesToRender = chart.interpretPointAsValuesForTooltip(point);
                const decodedValuesToRender = TooltipFormatter.decodeValuesToRender(valuesToRender);
                return {
                    values: decodedValuesToRender,
                    seriesName: decodedSeriesName,
                    seriesCustomData: point.series.options ? (point.series.options as any).customData : null,
                    seriesDataIndex: point.series.data.indexOf(point),
                    color: point.color
                } as Charts_Contracts.DataPoint;
            });

            const mappingData =
                typeof that._options.tooltipMapping === "function"
                    ? that._options.tooltipMapping(dataPoints)
                    : chart.mapDataPointsToTooltipLineItems(dataPoints);

            let outputHtml = "";
            let key = TooltipFormatter.isSharedTooltipContext(context)
                ? chart.getTooltipHeaderValue(context.points[0])
                : chart.getTooltipHeaderValue(context);

            if (typeof that._options.tooltipHeader === "function") {
                const tooltipHeader = that._options.tooltipHeader(key);
                let tooltipHeaderString: string;

                tooltipHeaderString = typeof tooltipHeader === "number" ? tooltipHeader.toLocaleString(navigator.language) : tooltipHeader;
                key = encodeUntrustedString(tooltipHeaderString);

                // As custom tooltipHeader formatter is provided by the caller, ignore the datetime formatting flag
                isDateTime = false;
            }

            outputHtml = TooltipFormatter.buildTooltipHeader(key, isDateTime);

            mappingData.forEach(item => {
                outputHtml += TooltipFormatter.buildTooltipPoint(item);
            });

            return outputHtml;
        };

        return handler;
    }
}
